class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
     requestAnimationFrame(() => {
    initPriceSlider();
  });
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();


// shopif dawn default price filter js
// class PriceRange extends HTMLElement {
//   constructor() {
//     super();
//     this.querySelectorAll('input').forEach((element) => {
//       element.addEventListener('change', this.onRangeChange.bind(this));
//       element.addEventListener('keydown', this.onKeyDown.bind(this));
//     });
//     this.setMinAndMaxValues();
//   }

//   onRangeChange(event) {
//     this.adjustToValidValues(event.currentTarget);
//     this.setMinAndMaxValues();
//   }

//   onKeyDown(event) {
//     if (event.metaKey) return;

//     const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
//     if (!event.key.match(pattern)) event.preventDefault();
//   }

//   setMinAndMaxValues() {
//     const inputs = this.querySelectorAll('input');
//     const minInput = inputs[0];
//     const maxInput = inputs[1];
//     if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
//     if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
//     if (minInput.value === '') maxInput.setAttribute('data-min', 0);
//     if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
//   }

//   adjustToValidValues(input) {
//     const value = Number(input.value);
//     const min = Number(input.getAttribute('data-min'));
//     const max = Number(input.getAttribute('data-max'));

//     if (value < min) input.value = min;
//     if (value > max) input.value = max;
//   }
// }

// customElements.define('price-range', PriceRange);


class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
      
      // 🛑 STOP the 'input' event from bubbling up to the form while typing
      element.addEventListener('input', (event) => event.stopPropagation());
    });
    
    this.setMinAndMaxValues();

    // 🎯 Find your specific span button by its ID
    this.filterButton = document.getElementById('pricefilterbutton');
    if (this.filterButton) {
      this.filterButton.addEventListener('click', this.onApplyFilter.bind(this));
    }
  }

  onRangeChange(event) {
    // 🛑 STOP the 'change' event from bubbling up when clicking out of the input
    event.stopPropagation();
    
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

 adjustToValidValues(input) {
    const value = Number(input.value);
    
    // 🛠️ FIX: Use Math.floor for min and Math.ceil for max
    const min = Math.floor(Number(input.getAttribute('data-min')));
    const max = Math.ceil(Number(input.getAttribute('data-max')));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  // 🚀 Fire the event when the span is clicked
  onApplyFilter() {
    // Manually dispatch a bubbling event from this component.
    // The main Shopify FacetFiltersForm will catch this and apply the price filter.
    this.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

document.addEventListener('DOMContentLoaded', initPriceSlider);

function initPriceSlider() {
  // ───── DESKTOP ELEMENTS ─────
  const sliderDesktop = document.getElementById('drag-range');
  const minInputDesktop = document.querySelector('input[id*="-GTE"]:not([id*="Mobile"])');
  const maxInputDesktop = document.querySelector('input[id*="-LTE"]:not([id*="Mobile"])');

  // ───── MOBILE ELEMENTS ─────
  const sliderMobile = document.getElementById('drag-range-mobile');
  const minInputMobile = document.getElementById('Mobile-Filter-Price-GTE');
  const maxInputMobile = document.getElementById('Mobile-Filter-Price-LTE');

  const filterButton = document.getElementById('pricefilterbutton');

  // Initialize Desktop Slider
  if (sliderDesktop && minInputDesktop && maxInputDesktop && typeof noUiSlider !== 'undefined') {
    setupSlider(sliderDesktop, minInputDesktop, maxInputDesktop);
  }

  // Initialize Mobile Slider
  if (sliderMobile && minInputMobile && maxInputMobile && typeof noUiSlider !== 'undefined') {
    setupSlider(sliderMobile, minInputMobile, maxInputMobile);
  }

  // ───── SPAN CLICK → TRIGGER SHOPIFY FACETS ─────
  if (filterButton) {
    filterButton.addEventListener('click', () => {
      // Because we now sync all inputs globally (see below), we only need to trigger 
      // the events on one set of inputs to force Shopify to update.
      if (minInputDesktop && maxInputDesktop) {
        triggerUserEvent(minInputDesktop);
        triggerUserEvent(maxInputDesktop);
      } else if (minInputMobile && maxInputMobile) {
        triggerUserEvent(minInputMobile);
        triggerUserEvent(maxInputMobile);
      }
    });
  }

  // Mobile button triggers the main span
  const mobileButton = document.getElementById('filtermobilebutton');
  if (mobileButton && filterButton) {
    mobileButton.addEventListener('click', (e) => {
      e.preventDefault();
      filterButton.click(); 
    });
  }
}

/* ───── REUSABLE SLIDER SETUP FUNCTION ───── */
/* ───── REUSABLE SLIDER SETUP FUNCTION ───── */
function setupSlider(sliderElement, minInput, maxInput) {
  // 🛠️ FIX: Read raw attributes first
  const minRaw = parseNumber(minInput.getAttribute('data-min'), 0);
  const maxRaw = parseNumber(maxInput.getAttribute('data-max'), null);
  if (maxRaw === null) return;

  // 🛠️ FIX: Always round DOWN minimums and round UP maximums
  const min = Math.floor(minRaw);
  const max = Math.ceil(maxRaw);

  const startMin = minInput.value ? Math.floor(parseNumber(minInput.value, min)) : min;
  const startMax = maxInput.value ? Math.ceil(parseNumber(maxInput.value, max)) : max;

  if (sliderElement.noUiSlider) sliderElement.noUiSlider.destroy();

  noUiSlider.create(sliderElement, {
    start: [startMin, startMax],
    connect: true,
    range: { min, max },
    step: 1
  });

  let isUpdating = false;

  // SLIDER → INPUTS
  sliderElement.noUiSlider.on('update', (values) => {
    isUpdating = true;
    
    // 🛠️ FIX: Round down the lower handle, round up the upper handle
    const minVal = Math.floor(values[0]);
    const maxVal = Math.ceil(values[1]);

    // Update the inputs directly connected to this slider
    minInput.value = minVal;
    minInput.setAttribute('value', minVal);
    maxInput.value = maxVal;
    maxInput.setAttribute('value', maxVal);

    // CRITICAL FIX: Sync ALL price inputs across the page (Desktop + Mobile)
    if (minInput.name) syncAllInputsByName(minInput.name, minVal);
    if (maxInput.name) syncAllInputsByName(maxInput.name, maxVal);

    isUpdating = false;
  });

  // INPUTS → SLIDER
  minInput.addEventListener('input', () => {
    if (isUpdating) return;
    sliderElement.noUiSlider.set([clamp(Math.floor(parseNumber(minInput.value, min)), min, max), null]);
  });

  maxInput.addEventListener('input', () => {
    if (isUpdating) return;
    sliderElement.noUiSlider.set([null, clamp(Math.ceil(parseNumber(maxInput.value, max)), min, max)]);
  });
}

/* ───── HELPERS ───── */
function parseNumber(value, fallback) {
  if (value == null) return fallback;
  const num = Number(value.toString().replace(/,/g, '').trim());
  return isNaN(num) ? fallback : num;
}

function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max);
}

// New helper to keep mobile and desktop completely synchronized 
function syncAllInputsByName(nameAttribute, value) {
  document.querySelectorAll(`input[name="${nameAttribute}"]`).forEach(input => {
    input.value = value;
    input.setAttribute('value', value);
  });
}

/* ───── SIMULATE REAL USER EVENTS ───── */
function triggerUserEvent(input) {
  // CRITICAL FIX: Removed mousedown/mouseup/mouseleave/click. 
  // Firing too many events causes Shopify's Javascript to abort/restart its AJAX requests,
  // causing the "first click fails, second click works" glitch.
  input.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
  input.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
}

document.addEventListener('DOMContentLoaded', () => {
  // 1. Grab both elements from the DOM
  const mobileButton = document.getElementById('filtermobilebutton');
  const filterSpan = document.getElementById('pricefilterbutton');

  // 2. Make sure both elements exist on the page before adding the event
  if (mobileButton && filterSpan) {
    
    // 3. Listen for the click on the mobile button
    mobileButton.addEventListener('click', () => {
      
      // 4. Trigger the click on the span
      filterSpan.click(); 
      
    });
  }
});
document.addEventListener('DOMContentLoaded', () => {
 document.querySelectorAll('.custom-video').forEach(video => {
  const box = video.parentElement;
  const button = box.querySelector('.play-toggle');

  function toggle() {
    if (video.paused) {
      video.play();
      box.classList.add('playing');
    } else {
      video.pause();
      box.classList.remove('playing');
    }
  }

  video.addEventListener('click', toggle);
  button.addEventListener('click', toggle);

  video.addEventListener('pause', () => {
    box.classList.remove('playing');
    button.classList.remove('playing');
  });

  video.addEventListener('play', () => {
    box.classList.add('playing');
    button.classList.add('playing');
  });
});
});